import { useQueries, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useMemo } from 'react'
import { contractClient } from './contract'

/**
 * Video metadata (duration, dimensions) is immutable once a media is
 * registered — re-fetching within a session adds latency for no benefit.
 * 1h TTL keeps the cache alive across typical editing sessions while
 * still expiring if the user revisits a day later.
 */
const VIDEO_META_CACHE_MS = 60 * 60_000

/**
 * Resolve per-input video durations, preferring a locally captured duration
 * (from upload probe or GraphQL media) and falling back to the oRPC media
 * info endpoint when absent / zero. `allResolved` is false while any fallback
 * fetch is still pending, so callers can gate pricing that depends on a full
 * set of durations.
 */
export function useReferenceVideoDurations(
  videoMediaIds: readonly string[],
  getLocalDuration: (mediaId: string) => number | undefined,
): { durations: number[]; allResolved: boolean } {
  const missing = useMemo(
    () =>
      videoMediaIds.filter(id => {
        const local = getLocalDuration(id)
        return !(typeof local === 'number' && local > 0)
      }),
    [videoMediaIds, getLocalDuration],
  )

  const results = useQueries({
    queries: missing.map(id => ({
      queryKey: ['mediaGetMediaInfo', id] as const,
      staleTime: VIDEO_META_CACHE_MS,
      gcTime: VIDEO_META_CACHE_MS,
      meta: { dependsOn: ['login'] },
      /**
       * 404 is terminal — the media was deleted, so retrying won't help
       * and we must let the caller move past the pending state. The
       * default 3-retry policy would block pricing for ~4 seconds.
       */
      retry: (_failureCount: number, error: unknown) => {
        const status = (error as { status?: number } | undefined)?.status
        if (status === 404) return false
        return _failureCount < 1
      },
      queryFn: async () => contractClient.media.getMediaInfo({ mediaId: id }),
    })),
    combine: rows => {
      const map = new Map<string, number>()
      const settled = new Set<string>()
      let pending = false
      rows.forEach((q, i) => {
        const sec = q.data?.durationSeconds
        if (typeof sec === 'number' && sec > 0) {
          map.set(missing[i], sec)
          settled.add(missing[i])
        } else if (q.isError) {
          /**
           * Treat errored fetches as resolved with 0 contribution so a
           * deleted/expired reference (404 MEDIA_NOT_FOUND) doesn't block
           * the pricing UI indefinitely. Server-side validation rejects
           * the submission anyway, so an inaccurate local price for an
           * invalid ref is not a realistic concern.
           */
          settled.add(missing[i])
        } else if (q.isPending || q.isFetching) {
          pending = true
        }
      })
      return { map, settled, pending }
    },
  })

  return useMemo(() => {
    const out: number[] = []
    let allResolved = !results.pending
    for (const id of videoMediaIds) {
      const local = getLocalDuration(id)
      if (typeof local === 'number' && local > 0) {
        out.push(local)
        continue
      }
      const fetched = results.map.get(id)
      if (typeof fetched === 'number' && fetched > 0) {
        out.push(fetched)
        continue
      }
      out.push(0)
      if (!results.settled.has(id)) {
        allResolved = false
      }
    }
    return { durations: out, allResolved }
  }, [videoMediaIds, getLocalDuration, results])
}

/**
 * Generate-or-get a still image `mediaId` for a video, so callers can render
 * a thumbnail for reference videos whose GraphQL media payload does not
 * include a pre-baked thumbnail URL. Result is cached per video mediaId.
 */
export function useReferenceVideoThumbnailId(videoMediaId: string | undefined) {
  return useQuery({
    enabled: !!videoMediaId,
    queryKey: ['mediaGetVideoThumbnailId', videoMediaId] as const,
    staleTime: VIDEO_META_CACHE_MS,
    gcTime: VIDEO_META_CACHE_MS,
    meta: { dependsOn: ['login'] },
    queryFn: async () => {
      if (!videoMediaId) return undefined
      const data = await contractClient.media.getVideoThumbnailId({
        videoMediaId,
      })
      return data?.imageMediaId
    },
  })
}

/**
 * Resolve a playable file URL for an audio `mediaId`. The GraphQL `media()`
 * resolver has no audio support, so prefilled audio references (e.g. from
 * history or a shared link) cannot reuse the Apollo media cache and must
 * fetch the URL via this oRPC endpoint.
 */
export function useAudioMediaUrl(audioMediaId: string | undefined) {
  return useQuery({
    enabled: !!audioMediaId,
    queryKey: ['mediaGetAudioUrl', audioMediaId] as const,
    staleTime: VIDEO_META_CACHE_MS,
    gcTime: VIDEO_META_CACHE_MS,
    meta: { dependsOn: ['login'] },
    queryFn: async () => {
      if (!audioMediaId) return undefined
      const data = await contractClient.media.getAudioUrl({ audioMediaId })
      return data?.fileUrl
    },
  })
}

export interface UploadAudioResult {
  id: string
  fileUrl: string
  duration: number
  size: number
  bitrate: number
  codec: string
}

/**
 * Upload an audio file via the v2 media ORPC endpoints.
 * Flow: request presigned S3 PUT url → upload raw file → register by externalId.
 *
 * Progress milestones emitted by `opts.onProgress` (fractional, `[0, 1]`):
 * - `0` on start
 * - `0.15` after presign succeeds (presign is a single fast RPC, worth ~15%)
 * - `0.15 → 0.9` linear ramp driven by the raw S3 PUT upload (75% slice,
 *   the dominant cost)
 * - `0.9` after the PUT completes, before the register RPC
 * - `1` after register succeeds
 * The split is heuristic — chosen so the bar never stalls at 0% on slow
 * presigns and never jumps to 100% before register confirms.
 *
 * @param file - Audio file to upload.
 * @param opts.isEphemeral - When true, the upload is marked private/ephemeral
 *   on the media service (used for short-lived references).
 * @param opts.onProgress - Receives fractional progress in `[0, 1]` at the
 *   milestones described above.
 * @param opts.signal - Abort signal forwarded to every network call.
 */
export async function uploadAudioMedia(
  file: File,
  opts: {
    isEphemeral?: boolean
    onProgress?: (progress: number) => void
    signal?: AbortSignal
  } = {},
): Promise<UploadAudioResult> {
  const { onProgress, signal, isEphemeral } = opts
  onProgress?.(0)

  const urlData = await contractClient.media.getAudioUploadUrl(
    { isEphemeral },
    { signal },
  )
  if (!urlData) throw new Error('Failed to get audio upload url')
  onProgress?.(0.15)

  await axios.request({
    method: 'PUT',
    url: urlData.uploadUrl,
    data: file,
    /** Match the Content-Type the S3 presigned URL was signed against. */
    headers: file.type ? { 'Content-Type': file.type } : undefined,
    signal,
    onUploadProgress: e => {
      if (e.progress) onProgress?.(0.15 + e.progress * 0.75)
    },
  })
  onProgress?.(0.9)

  const regData = await contractClient.media.registerAudio(
    { externalId: urlData.externalId },
    { signal },
  )
  if (!regData) throw new Error('Failed to register audio')
  onProgress?.(1)

  return {
    id: regData.id,
    fileUrl: regData.fileUrl,
    duration: regData.duration,
    size: regData.size,
    bitrate: regData.bitrate,
    codec: regData.codec,
  }
}
